import { useCallback, useState } from 'react';
import { View } from 'react-native';
import { Flex, Typography, useScreenSize } from '@banhala/ui-kit';
import { type NavigationProp, useNavigation } from '@react-navigation/native';
import type { ThreeColFilterProps, ThreeColFilterItemWithLogging } from './types';
import { useLogAnalyticsEvent } from '../../../../hooks/useLogAnalyticsEvent';
import { useFilterListAtom } from '../FilterList/Provider/filterList/useFilterListAtom';

export const ThreeColFilter = ({
  body,
  logging,
  scrollToFilterList,
}: ThreeColFilterProps) => {
  const navigation = useNavigation<
    NavigationProp<{
      [route: string]: Record<string, string[] | undefined>;
    }>
  >();
  const { setFilterListAtom } = useFilterListAtom();
  const logAnalyticsEvent = useLogAnalyticsEvent();
  const { width: screenWidth } = useScreenSize();
  const [selectedValue, setSelectedValue] = useState(body?.selected_value ?? '');

  const handleSelect = useCallback(
    (item: ThreeColFilterItemWithLogging) => {
      logAnalyticsEvent({
        name: 'CLICK_FILTER',
        analytics: { ...logging?.analytics, ...item.logging?.analytics },
        ably_only_log: { ...logging?.ably_only_log, ...item.logging?.ably_only_log },
      });

      setSelectedValue(item.item.query_value);
      scrollToFilterList();

      if (body?.query_name) {
        setFilterListAtom(prev => ({
          ...prev,
          [body.query_name]: [item.item.query_value],
        }));
        navigation.setParams({
          [body.query_name]: [item.item.query_value],
        });
      }
    },
    [logging, body, scrollToFilterList, setFilterListAtom, navigation, logAnalyticsEvent],
  );

  if (!body || body.possible_items.length === 0) return null;

  const cellWidth = Math.floor(screenWidth / 3);

  return (
    <Flex borderTopWidth={1} borderColor="border_tertiary">
      <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
        {body.possible_items.map((item, index) => {
          const isSelected = item.item.query_value === selectedValue;
          const col = index % 3;
          return (
            <Flex
              key={item.item.query_value}
              width={cellWidth}
              height={43}
              justifyContent="center"
              padding={{ horizontal: 16 }}
              borderBottomWidth={1}
              borderColor="border_tertiary"
              {...(col < 2 && { borderRightWidth: 1 })}
              onPress={() => handleSelect(item)}
            >
              <Typography
                type={isSelected ? 'subtitle2' : 'body2'}
                color={isSelected ? 'content_primary' : 'content_tertiary'}
              >
                {item.item.text}
              </Typography>
            </Flex>
          );
        })}
      </View>
    </Flex>
  );
};
